import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { GenericError } from "@via/components/error.tsx";
import Table, { TableSize } from "@via/components/table/table";
import TagsCell from "@via/components/tags-cell.tsx";
import { TruncatedText } from "@via/components/truncated-text.tsx";
import useUrlParams from "@via/hooks/useURLParams";
import { EMPTY_TABLE_MESSAGE, ERROR } from "@via/utils/constants";
import { formatDateTimeTwoLines } from "@via/utils/helpers";
import { ActivityRunResponse } from "@via/utils/interfaces";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useDebounceCallback, useDebounceValue } from "usehooks-ts";
import RunPipelineLink from "@via/pages/projects/runs/run-pipeline-link";
import { getRunStatusLabel } from "@via/pages/projects/runs/utils/helpers";
import { useActivityRunList } from "@via/pages/activity/activity.api";
import RunsAdvancedFilter from "@via/pages/runs/components/runs-advanced-filter";
import { FILTER_FIELD_OPTIONS } from "@via/hooks/runs-filter-types.ts";
import { useRunsAdvancedFilter } from "@via/hooks/useRunsAdvancedFilter.ts";

const defaultParams = {
  sort: [{ id: "dateCreatedLastRun", desc: true }],
  page: 0,
  pageSize: 10,
};

interface ActivityRunListProps {
  pipelineId?: string;
  onDataChange?: (
    data: { data: ActivityRunResponse[]; total: number } | undefined
  ) => void;
}

function ActivityRunList({
  pipelineId,
  onDataChange,
}: ActivityRunListProps): JSX.Element {
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );

  const [searchKey, setSearchKey] = useState("");
  const [debouncedSearchKey, setDebouncedSearchKey] = useDebounceValue(
    searchKey,
    300
  );

  const {
    isOpen: isFilterOpen,
    setIsOpen: setFilterOpen,
    openPanel: openFilterPanel,
    criteria,
    addCriteria,
    removeCriteria,
    updateCriteria,
    applyFilters,
    clearAll: clearFilters,
    hasActiveCriteria,
    appliedFilters,
  } = useRunsAdvancedFilter();

  const combinedFilters = useMemo(() => {
    const base = pipelineId ? [{ id: "pipelineId", value: pipelineId }] : [];
    return [...base, ...appliedFilters];
  }, [pipelineId, appliedFilters]);

  const runsColumnHelper = createColumnHelper<ActivityRunResponse>();
  const runListColumns = [
    runsColumnHelper.accessor("id", {
      size: 60,
      minSize: 40,
      maxSize: 80,
      cell: ({ row }) => {
        return (
          <Link to={`/runs/${row.original.id}`} className="via-link truncate">
            {row.original.id}
          </Link>
        );
      },
      header: () => <span>Run ID</span>,
    }),
    runsColumnHelper.accessor("name", {
      size: 200,
      minSize: 150,
      maxSize: 400,
      cell: ({ row }) => {
        return (
          <Link to={`/runs/${row.original.id}`} className="via-link truncate">
            <TruncatedText
              text={row.original.name}
              maxLength={40}
              id={`run-tooltip-${row.original.id}-${row.original.name}`}
            />
          </Link>
        );
      },
      header: () => <span>Run Name</span>,
    }),
    runsColumnHelper.accessor("status", {
      size: 180,
      minSize: 150,
      cell: ({ row }) => {
        const run = row.original;
        return (
          <div className="flex items-center gap-2">
            {getRunStatusLabel(run.status)}
          </div>
        );
      },
      header: () => <span>Status</span>,
    }),
    runsColumnHelper.accessor("pipelineName", {
      size: 180,
      minSize: 150,
      maxSize: 300,
      // A standalone process run's pipelineId is a synthesized one node pipeline, and the
      // pipeline editor refuses to open it, so this cell must not link there. RunPipelineLink
      // owns that rule for every surface that renders a run's pipeline reference; do not
      // re-inline a <Link> here.
      cell: ({ row }) => {
        return (
          <RunPipelineLink
            pipelineId={row.original.pipelineId}
            pipelineName={row.original.pipelineName}
            subjectType={row.original.subjectType}
            sourceProcessId={row.original.sourceProcessId}
          />
        );
      },
      header: () => <span>Pipeline Name</span>,
    }),
    runsColumnHelper.accessor("pipelineVersion", {
      size: 60,
      minSize: 40,
      maxSize: 80,
      cell: ({ row }) => {
        return <span className="truncate">{row.original.pipelineVersion}</span>;
      },
      header: () => <span>Pipeline Version</span>,
      enableSorting: false,
    }),
    runsColumnHelper.accessor("username", {
      size: 150,
      minSize: 120,
      maxSize: 200,
      cell: ({ row }) => {
        return <span className="truncate">{row.original.username}</span>;
      },
      header: () => <span>Owner</span>,
    }),
    runsColumnHelper.display({
      id: "tags",
      size: 180,
      minSize: 120,
      maxSize: 300,
      cell: ({ row }) => (
        <TagsCell tags={row.original.tags ?? []} rowId={row.original.id} />
      ),
      header: () => <span>Tags</span>,
      enableSorting: false,
    }),
    runsColumnHelper.accessor("dateCreated", {
      size: 140,
      minSize: 100,
      maxSize: 200,
      cell: ({ row }) => formatDateTimeTwoLines(row.original.dateCreated),
      header: () => <span>Date Created</span>,
    }),
    runsColumnHelper.accessor("dateCreatedLastRun", {
      size: 140,
      minSize: 100,
      maxSize: 200,
      cell: ({ row }) =>
        formatDateTimeTwoLines(row.original.dateCreatedLastRun),
      sortDescFirst: true,
      header: () => <span>Last Run</span>,
    }),
  ] as Array<ColumnDef<ActivityRunResponse>>;

  const {
    mutate: fetchActivityRunList,
    data: activityRunListData,
    isPending: isActivityRunListPending,
    isError: isActivityRunListError,
  } = useActivityRunList();

  useEffect(() => {
    setUrlParams({ page, pageSize, sort });
    fetchActivityRunList({
      page,
      pageSize,
      sort,
      searchKey,
      filter: combinedFilters,
    });
  }, [page, pageSize, sort, searchKey, combinedFilters]);

  // Notify parent when data changes
  useEffect(() => {
    if (activityRunListData) {
      onDataChange?.({
        data: activityRunListData.data,
        total: activityRunListData.total,
      });
    }
  }, [activityRunListData, onDataChange]);

  const debounced = useDebounceCallback(value => {
    setDebouncedSearchKey(value);
    setSearchKey(value);
    setPage(0);
    setPageSize(10);
  }, 300);

  const handleApplyFilters = () => {
    applyFilters();
    setPage(0);
  };

  const handleClearFilters = () => {
    clearFilters();
    setPage(0);
  };

  // Without this branch a failed fetch leaves `data` undefined, and the table falls through to
  // its empty state, telling the user they have no run history when the request simply failed.
  if (isActivityRunListError) {
    return (
      <div className="via-generic-error">
        <GenericError message={ERROR} />
      </div>
    );
  }

  return (
    <div>
      <Table
        isLoading={isActivityRunListPending}
        data={activityRunListData?.data}
        columns={runListColumns}
        size={TableSize.Big}
        showPagination={true}
        page={page}
        sort={sort}
        setSort={setSort}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        emptyMessage={EMPTY_TABLE_MESSAGE.RUNS}
        total={activityRunListData?.total}
        applyColumnSize
        additionalFilters={
          <div className="flex-grow">
            <div className="relative">
              <MagnifyingGlassIcon
                className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                type="text"
                placeholder="Search across all fields..."
                className="w-full rounded-md border-0 py-2 pl-10 pr-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                defaultValue={debouncedSearchKey}
                onChange={e => debounced(e.target.value)}
              />
            </div>
            <RunsAdvancedFilter
              isOpen={isFilterOpen}
              onToggle={() =>
                isFilterOpen ? setFilterOpen(false) : openFilterPanel()
              }
              criteria={criteria}
              fieldOptions={FILTER_FIELD_OPTIONS}
              onAddCriteria={addCriteria}
              onRemoveCriteria={removeCriteria}
              onUpdateCriteria={updateCriteria}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
              hasActiveCriteria={hasActiveCriteria}
            />
          </div>
        }
      />
    </div>
  );
}

export default ActivityRunList;
